import {TranslationObject} from '@colonist/i18n'
import {EnumUtils} from './utils/EnumUtils'
import * as gameBaseTypes from '@colonist/game-base-types'
import {MatchmakingAction, RoomType, ShuffleAction} from '@colonist/lobby-types'
import {ColonistMembershipType, GAME_STATE_UPDATE_ENUM_ID, LobbyImage, SOCKET_EVENT_ID} from '@colonist/types'
import {BeginnerModeLevel} from '@colonist/game-beginner-mode'

export module NetworkEnums {
    export enum State {
        GameStateUpdate = GAME_STATE_UPDATE_ENUM_ID,
        MatchmakingStateUpdate = 131,
        LobbyStateUpdate = 133,
        ChannelStateUpdate = 134,
        GeneralStateUpdate = 135,
        SocketMonitorUpdate = 136,
        RoomEvent = 137,
        SocketEvent = SOCKET_EVENT_ID,
        ShuffleStateUpdate = 139,
    }

    export interface SendMatchmakingActionData {
        action: MatchmakingAction
        payload: unknown
    }

    export interface SendShuffleActionData {
        action: ShuffleAction
        payload: unknown
    }

    export interface AccessGameLinkData {
        gameId: string
        clientVersion: number
    }

    export interface StartBotGameData {
        clientVersion: number
        bots: gameBaseTypes.BotDifficulty[]
        victoryPointsToWin?: number
    }

    export interface StartShuffleBotGameData {
        clientVersion: number
    }

    export interface StartBeginnerModeData extends StartBotGameData {
        level: BeginnerModeLevel
        map: MapSettingEnum
    }

    export interface ClickedCreateRoomData {
        roomType: RoomType
        clientVersion: number
    }

    export enum MatchmakingState {
        MatchmakingUpdate = 1,
        MatchmakingError = 2,
        MatchmakingShowPreRollAd = 3, // Unused
        MatchmakingDisableAdblock = 4,
        MatchmakingReconnect = 5,
    }

    export enum ShuffleState {
        ShuffleQueueData = 1,
        ShufflePlayerData = 2,
    }

    export interface MatchmakingStateUpdateData {
        type: MatchmakingState
        payload: unknown
    }

    export interface ShuffleStateUpdateData {
        type: ShuffleState
        payload: unknown
    }

    export interface GeneralStateUpdateData {
        type: GeneralState
        payload: unknown
    }

    export enum GeneralState {
        UpdateOnlineStatus = 1,
        ReceivedRoomInvites = 2,
        RoomInviteResponse = 3,
        RemoveRoomInvite = 4,
        RoomInviteSentSuccessfully = 5,
        Notifications = 6,
        MarkedAsRead = 7,
        ReceivedFriendRequest = 8,
    }

    export enum LobbyState {
        AutoRejoinGame = 1,
        SessionState = 2,
        UserStateUpdate = 3,
        LobbyDisconnectData = 4,
        SendPopup = 5,
        ShowCornerPopup = 6,
        SendBroadcast = 7,
        SocketTest = 8,
        ServerUpdating = 9,
        ReloadClient = 10,
        ServerUpdated = 11,
        RoomListUpdate = 12,
        ItemPurchaseSuccessPopup = 13,
    }

    export interface LobbyStateUpdateData {
        type: LobbyState
        payload: unknown
    }

    export enum ChannelState {
        ChannelRoomUpdate = 1,
    }

    export interface ChannelStateUpdateData {
        type: ChannelState
        payload: unknown
    }

    export interface ClientReloadData {
        delay: number
    }

    export interface ServerUpdatedData {
        newServerVersion: number
    }

    export interface RoomListUpdateData {
        readonly full: boolean
        readonly rooms: RoomUpdateData[]
    }

    export interface RoomUpdatePlayerData {
        readonly username: string
        readonly icon: LobbyImage
        readonly isBot: boolean
        readonly membership: ColonistMembershipType | undefined
        readonly karmaCompletedGames: number | undefined
        readonly karmaTotalGames: number | undefined
    }

    export interface RoomUpdateData {
        readonly id: string
        readonly players: RoomUpdatePlayerData[]
        readonly maxPlayers: number
        readonly mode: ModeSettingEnum
        readonly map: MapSettingEnum
        readonly speed: PlayerEnums.GameSpeedSettings
        readonly minimumKarma: number
        readonly modePlayable: boolean
        readonly mapPlayable: boolean
        readonly visible: boolean
    }
}

export module GameStateEnums {
    export enum UIGameEventEnums {
        FinishedBuildingGame,
        GameStateUpdated, PlayerControllerStateUpdated, DiceStateUpdated, BankStateUpdated, BankTradeRatioStateUpdated, MapStateUpdated,
        KarmaUpdated, CanResign,
        AmountOfCardsToDiscardUpdated, AmountOfCardsToSelect,
        SelectCardsToDiscard, SelectCardsFromBankUsingDevelopmentCard, SelectCardsForPendingDistribution, SelectCardToGiveToAnotherPlayer,
        ConfirmUseDevelopmentCard,
        StealDevelopmentCardFromPlayer, StealResourceCardFromPlayer,
        SelectPlayer, SelectPlayerForDevelopmentCard, SelectPlayerAndCard,
        SelectDice,
        GivePlayerCardFromTile, ExchangeCards, GivePlayerGrantedCards,
        CloseTradeUI, ShowTradeUI, TradeStateUpdated,
        EmbargoStatusUpdated, RemoveTradeOffer, TradeResetStateAtEndOfTurn, TradeResponseState, NewTradeOfferReceived, TradePlayersCreatingCounterOfferState,
        BoldTextInTradeWindow,
        CloseDiscardCardUI, ShowDiscardCardUI,
        ClosePopupUI,
        SpecialBuildPhaseStateUpdated,
        SetRoadEdgeHighlight, SetShipEdgeHighlight, SetCornerHighlight, SetTileHighlight, HighlightDiceNumbersForInventor,
        VoteReceived,
        PlayerReceivedAchievement,
        CanvasResized, ClickedMultiButton,
        SpectatorDataUpdated,
        KnightStateUpdated,
        CityImprovementStateReceived, CityImprovementStateUpdated, ForceShowCityImprovementButtons,
        MerchantStateUpdated,
        GameEnd, GameEndRematchTextUpdated,
        RobberMoved, MerchantBankTradeRatioActive,
        GamePauseToggled, NewChatMessageReceived,
        TemporaryPlayerStuckInEmbargoState,
        BeginnerHintReceived,
        HighlightTradeOffer, PopupActive, FeedbackFormSubmitted, FloatingNotification,
        BeginnerModeStart, BeginnerModeInteractWithGuide, BeginnerModeForceOpenGuideBubble, BeginnerModeActionUpdated, BeginnerModeLevelEnd, GameInformationPopupActive, BeginnerModeClickedDisabledElement,
        SuggestAction, SelectDiceNumbersConfirmation,

        DiceNumberAnimationEnd, TileNumberAnimationEnd, CardAnimationEnd,

        ClientReconnectionTriggered,

        // GameVisibilityUpdated tracks when a user is actively engaged in the game
        // Web: Triggers when user switches tabs
        // Mobile: Triggers when user moves app to background
        // Discord: Triggers when user moves activity to background
        GameVisibilityUpdated,
    }
}

/**
 * @deprecated Import GameTypeEnum from @colonist/game-base-types
 */
export import GameTypeEnum = gameBaseTypes.GameTypeEnum

/**
 * @deprecated Import ModeSettingEnum from @colonist/game-base-types
 */
export import ModeSettingEnum = gameBaseTypes.ModeSettingEnum

export enum RankedModeTypes {
    Classic4P = 0,
    Classic1v1 = 1,
    Seafarerers4P = 2,
    CitiesAndKnights4P = 3,
    Rush4P = 4,
}

/**
 * @deprecated Import ELOTypeEnum from @colonist/game-base-types
 */
export import ELOTypeEnum = gameBaseTypes.ELOTypeEnum

export enum RankedSeasonTypes {
    Development = 0,
    Alpha = 1,
    Beta1 = 2,
    Beta2 = 3,
    Season1 = 4,
    Season2 = 5,
    Season3 = 6,
    Season4 = 7,
    Season5 = 8,
    Season6 = 9,
    Season7 = 10,
    Season8 = 11,
    Season9 = 12,
    Season10 = 13,
    Season11 = 14,
    Season12 = 15,
    Season13 = 16,
    Season14 = 17,
    Season15 = 18,
    Season16 = 19,
}

/**
 * @deprecated Import ExtensionSettingEnum from @colonist/game-base-types
 */
export import ExtensionSettingEnum = gameBaseTypes.ExtensionSettingEnum

export function getExtensionSettingText(extensionSetting: ExtensionSettingEnum): TranslationObject {
    const extensionSettingKey = EnumUtils.getEnumKey(extensionSetting, ExtensionSettingEnum)
    return {key: `strings:game.settings.gameInfoTab.extension.options.${extensionSettingKey}`}
}

/**
 * @deprecated Import ExtensionSettingEnum from @colonist/game-base-types
 */
export import ScenarioSettingEnum = gameBaseTypes.ScenarioSettingEnum

export function getScenarioSettingText(scenarioSetting: ScenarioSettingEnum): TranslationObject {
    const scenarioSettingKey = EnumUtils.getEnumKey(scenarioSetting, ScenarioSettingEnum)
    return {key: `strings:colonistCommon.gameMaps.seafarers.scenarios.${scenarioSettingKey}`}
}

/**
 * @deprecated Import MapSettingEnum from @colonist/game-base-types
 */
export import MapSettingEnum = gameBaseTypes.MapSettingEnum

/**
 * @deprecated Import DiceSettingEnum from @colonist/game-base-types
 */
export import DiceSettingEnum = gameBaseTypes.DiceSettingEnum
import {GAME_TIMER_OPTIONS} from '@colonist/shared-types'

export function getDiceSettingText(diceSetting: DiceSettingEnum): TranslationObject {
    switch(diceSetting) {
        case DiceSettingEnum.Balanced:
            return {key: `strings:colonistCommon.DiceSettingEnum.Balanced`}
        case DiceSettingEnum.Random:
            return {key: `strings:colonistCommon.DiceSettingEnum.Random`}
        case DiceSettingEnum.PredefinedDiceSequenceDeleted:
            return {key: `strings:colonistCommon.utils.bug`}
        case DiceSettingEnum.StressTestSequence:
            return {key: `strings:colonistCommon.utils.bug`}
    }
}

export enum RankedConstants {
    NumberOfGamesToUnlockRanked = 5,
}

export module PlayerEnums {
    /**
     * @deprecated Import GameSpeedSettings from @colonist/game-base-types
     */
    export import GameSpeedSettings = gameBaseTypes.GameSpeedSettings

    export function getSpeedSettingText(gameSpeedSetting: GameSpeedSettings): TranslationObject {
        const allGameSpeedSettings = GAME_TIMER_OPTIONS
        const option = allGameSpeedSettings[gameSpeedSetting]
        return {key: option.labelTranslationKey}
    }

    export enum RoomJoinStatus {
        Joined,
        CannotJoinKicked,
        CannotJoinFull,
        CannotJoinKarma
    }

    export enum ChannelStatus {
        Game,
        Room,
        Closed,
        Reconnect
    }

    export enum RoomInfoMessage {
        None,
        ClickReady,
        PremiumItemSelected
    }

    export class PlayerConstants {
        static readonly MIN_USERNAME_LENGTH = 3
        static readonly MAX_USERNAME_LENGTH = 12
        static readonly MAX_GUEST_USERNAME_LENGTH = 6
    }

}
